import React, { useState, useRef } from 'react';
import { useCampaign } from '../../contexts/CampaignContext';
import { MONSTER_PACKS, THEMES } from '../../lib/monsterPacks';
import { monsterPackKey } from '../../lib/monsterPackMap';
import BuffPanel from './BuffPanel';
import Tooltip from './Tooltip';
import InventoryPanel from '../player/InventoryPanel';
const CONDITIONS = [
  { id: 'blinded',       label: 'Blinded',       icon: '👁', desc: "Can't see. Auto-fail sight checks. Attacks have disadvantage; attacks against have advantage." },
  { id: 'charmed',       label: 'Charmed',        icon: '💕', desc: "Can't attack the charmer. Charmer has advantage on social checks against creature." },
  { id: 'deafened',      label: 'Deafened',       icon: '👂', desc: "Can't hear. Auto-fail hearing checks." },
  { id: 'exhaustion',    label: 'Exhausted',      icon: '😩', desc: "Levels 1–6: disadvantage on checks, speed halved, disadvantage on attacks/saves, HP max halved, speed 0, death." },
  { id: 'frightened',    label: 'Frightened',     icon: '😨', desc: "Disadvantage on ability checks and attacks while source of fear is in line of sight. Can't willingly move closer." },
  { id: 'grappled',      label: 'Grappled',       icon: '🤼', desc: "Speed becomes 0. Ends if grappler is incapacitated or forced apart." },
  { id: 'incapacitated', label: 'Incapacitated',  icon: '💫', desc: "Can't take actions or reactions." },
  { id: 'invisible',     label: 'Invisible',      icon: '👻', desc: "Impossible to see without magic or special sense. Attacks against have disadvantage; creature's attacks have advantage." },
  { id: 'paralyzed',     label: 'Paralyzed',      icon: '⚡', desc: "Incapacitated, can't move or speak. Auto-fail STR/DEX saves. Attacks have advantage; melee hits are auto-crits." },
  { id: 'petrified',     label: 'Petrified',      icon: '🗿', desc: "Turned to stone. Weight ×10. Incapacitated, can't move or speak. Resistance to all damage. Immune to poison/disease." },
  { id: 'poisoned',      label: 'Poisoned',       icon: '☠️', desc: "Disadvantage on attack rolls and ability checks." },
  { id: 'prone',         label: 'Prone',          icon: '⬇️', desc: "Can only crawl (costs extra movement). Disadvantage on attacks. Melee attacks against have advantage; ranged have disadvantage." },
  { id: 'restrained',    label: 'Restrained',     icon: '⛓', desc: "Speed 0. Attacks have disadvantage. Attacks against have advantage. Disadvantage on DEX saves." },
  { id: 'stunned',       label: 'Stunned',        icon: '💥', desc: "Incapacitated, can't move, can only speak falteringly. Auto-fail STR/DEX saves. Attacks against have advantage." },
  { id: 'unconscious',   label: 'Unconscious',    icon: '💤', desc: "Incapacitated, can't move or speak, drops held items, falls prone. Auto-fail STR/DEX saves. Attacks have advantage; melee hits are auto-crits." },
  { id: 'burrowed',      label: 'Burrowed',       icon: '⬇️', desc: "Creature is underground. Can't be targeted by most attacks. May have tremorsense." },
];

function hpColor(hp, maxHp) {
  if (hp <= 0) return '#4b5563';
  const pct = hp / maxHp;
  if (pct > 0.5)  return '#5cb870';
  if (pct > 0.35) return '#d4943a';
  if (pct > 0.15) return '#d4943a';
  return '#e07070';
}

export default function TokenInspector({ token }) {
  const { updateToken, players, assignToken, tokens, removeToken, removeFromInitiative, updatePlayer } = useCampaign();

  const [damageInput,   setDamageInput]   = useState('');
  const [healInput,     setHealInput]     = useState('');
  const [hpInput,       setHpInput]       = useState('');
  const [maxHpInput,    setMaxHpInput]    = useState('');
  const [tempInput,     setTempInput]     = useState('');
  const [settingsOpen,  setSettingsOpen]  = useState(false);
  const [concCheck,     setConcCheck]     = useState(null); // { dc, buffLabel }
  const [showInventory, setShowInventory] = useState(false);

  if (!token) {
    return <div style={styles.empty}>Select a token on the map.</div>;
  }

  const { hp, maxHp = 10, tempHp = 0, conditions = [] } = token;
  const hpPct     = Math.max(0, hp / maxHp);
  const barColour = hpColor(hp, maxHp);

  // ── HP actions ─────────────────────────────────────────────────────────
  const buffs = token.buffs || [];

  const applyDamage = async () => {
    const dmg = parseInt(damageInput, 10);
    if (isNaN(dmg) || dmg <= 0) return;

    // If this token is a mirror image duplicate, destroy it instead of applying damage
    if (token.mirrorDuplicate && token.mirrorGroupId) {
      const realToken = Object.values(tokens).find(
        (t) => !t.mirrorDuplicate && t.mirrorGroupId === token.mirrorGroupId
      );
      await removeToken(token.id);
      if (realToken) {
        const realBuffs = realToken.buffs || [];
        const miBuff = realBuffs.find((b) => b.id === 'mirror_image');
        if (miBuff) {
          const newCharges = (miBuff.charges || 1) - 1;
          if (newCharges <= 0) {
            // Last duplicate — clean up remaining dupes and remove buff
            const remaining = Object.values(tokens).filter(
              (t) => t.mirrorDuplicate && t.mirrorGroupId === token.mirrorGroupId && t.id !== token.id
            );
            await Promise.all(remaining.map((t) => removeToken(t.id)));
            const cleaned = realBuffs.filter((b) => b.id !== 'mirror_image');
            await updateToken(realToken.id, { buffs: cleaned, mirrorGroupId: null });
          } else {
            const newBuffs = realBuffs.map((b) =>
              b.id === 'mirror_image' ? { ...b, charges: newCharges } : b
            );
            await updateToken(realToken.id, { buffs: newBuffs });
          }
        }
      }
      setDamageInput('');
      return;
    }

    let remaining = dmg;
    let newTemp   = tempHp;
    if (newTemp > 0) {
      const absorbed = Math.min(newTemp, remaining);
      newTemp -= absorbed;
      remaining -= absorbed;
    }
    const newHp = Math.max(0, hp - remaining);
    const updates = { hp: newHp, tempHp: newTemp };
    if (newHp <= 0 && token.type !== 'player') {
      // Auto-defeat non-player tokens at 0 HP
      updates.defeated = true;
      removeFromInitiative(token.id);
    } else if (newHp <= 0 && token.type === 'player') {
      if (hp <= 0 && token.deathSaves) {
        // Already at 0 HP — damage adds 2 automatic failures
        const ds = token.deathSaves;
        updates.deathSaves = { successes: ds.successes, failures: Math.min(3, ds.failures + 2) };
      } else if (hp > 0) {
        // Just dropped to 0 — enter death save mode
        updates.deathSaves = { successes: 0, failures: 0 };
      }
    }
    updateToken(token.id, updates);

    // Concentration check — if token has a concentration buff and took actual HP damage
    if (remaining > 0 && newHp > 0) {
      const concBuff = buffs.find((b) => b.concentration);
      if (concBuff) {
        const dc = Math.max(10, Math.floor(remaining / 2));
        setConcCheck({ dc, buffLabel: concBuff.label || concBuff.id, buffId: concBuff.id });
      }
    }

    setDamageInput('');
  };

  const applyHeal = () => {
    const amount = parseInt(healInput, 10);
    if (isNaN(amount) || amount <= 0) return;
    const newHp   = Math.min(maxHp, hp + amount);
    const updates = { hp: newHp };
    // Healing while dying clears death saves
    if (token.deathSaves && hp <= 0) updates.deathSaves = null;
    // Heal restores defeated monsters/NPCs to normal state
    if (newHp > 0 && token.defeated) updates.defeated = false;
    updateToken(token.id, updates);
    setHealInput('');
  };

  const applySetHp = () => {
    const val = parseInt(hpInput, 10);
    if (isNaN(val)) return;
    const newHp   = Math.max(0, Math.min(maxHp, val));
    const updates = { hp: newHp };
    if (newHp > 0 && token.defeated) updates.defeated = false;
    updateToken(token.id, updates);
    setHpInput('');
  };

  const applySetMaxHp = () => {
    const val = parseInt(maxHpInput, 10);
    if (isNaN(val) || val < 1) return;
    const newHp   = Math.min(hp, val);
    const updates = { maxHp: val, hp: newHp };
    if (newHp > 0 && token.defeated) updates.defeated = false;
    updateToken(token.id, updates);
    setMaxHpInput('');
  };

  const applyTempHp = () => {
    const val = parseInt(tempInput, 10);
    if (isNaN(val) || val < 0) return;
    // Doesn't stack — takes higher value
    updateToken(token.id, { tempHp: Math.max(tempHp, val) });
    setTempInput('');
  };

  // ── Condition toggle ────────────────────────────────────────────────────
  const toggleCondition = (condId) => {
    const has  = conditions.includes(condId);
    const next = has
      ? conditions.filter((c) => c !== condId)
      : [...conditions, condId];
    updateToken(token.id, { conditions: next });
  };

  const clearConditions = () => updateToken(token.id, { conditions: [] });

  return (
    <div style={styles.inspector}>
      {/* Token name + colour */}
      <div style={styles.tokenHeader}>
        <span style={{ ...styles.tokenDot, background: token.colour }} />
        <input
          key={token.id}
          style={styles.tokenNameInput}
          defaultValue={token.name}
          onBlur={(e) => {
            const v = e.target.value.trim();
            if (v && v !== token.name) {
              updateToken(token.id, { name: v });
            } else {
              e.target.value = token.name;
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.target.blur();
            if (e.key === 'Escape') { e.target.value = token.name; e.target.blur(); }
          }}
        />
        <span style={styles.tokenType}>{token.type}</span>
      </div>

      {/* HP bar */}
      <div style={styles.hpBarOuter}>
        <div style={{ ...styles.hpBarFill, width: `${hpPct * 100}%`, background: barColour }} />
        {tempHp > 0 && (
          <div style={{ ...styles.tempBar, width: `${Math.min(100, (tempHp / maxHp) * 100)}%` }} />
        )}
      </div>
      <div style={styles.hpNumbers}>
        <span style={{ color: barColour, fontWeight: 800, fontSize: 18 }}>{hp}</span>
        <span style={styles.hpSlash}> / {maxHp}</span>
        {tempHp > 0 && <span style={styles.tempDisplay}> +{tempHp} temp</span>}
      </div>

      {/* Concentration check prompt */}
      {concCheck && (
        <div style={styles.concCheckPanel}>
          <div style={styles.concCheckTitle}>⚡ Concentration Check</div>
          <div style={styles.concCheckDesc}>
            Constitution save <strong>DC {concCheck.dc}</strong> to maintain <strong>{concCheck.buffLabel}</strong>
          </div>
          <div style={styles.concCheckBtns}>
            <button
              type="button"
              className="btn-ghost btn-sm"
              style={{ flex: 1, color: '#4ade80', borderColor: '#4ade80' }}
              onPointerDown={(e) => { e.stopPropagation(); setConcCheck(null); }}
            >
              ✓ Pass
            </button>
            <button
              type="button"
              className="btn-ghost btn-sm"
              style={{ flex: 1, color: '#f87171', borderColor: '#f87171' }}
              onPointerDown={(e) => {
                e.stopPropagation();
                // Remove the concentration buff
                const newBuffs = buffs.filter((b) => b.id !== concCheck.buffId);
                updateToken(token.id, { buffs: newBuffs, concentration: false });
                setConcCheck(null);
              }}
            >
              ✕ Fail
            </button>
          </div>
        </div>
      )}

      {/* Damage / Heal */}
      <div style={styles.section}>
        <div style={styles.sectionLabel}>Damage / Heal</div>
        <div style={styles.inputRow}>
          <input
            style={styles.numInput}
            type="number"
            min="0"
            placeholder="0"
            value={damageInput}
            onChange={(e) => setDamageInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && applyDamage()}
          />
          <button className="btn-danger btn-sm" style={{ flex: 1 }} onClick={applyDamage}>
            Damage
          </button>
        </div>
        <div style={styles.inputRow}>
          <input
            style={styles.numInput}
            type="number"
            min="0"
            placeholder="0"
            value={healInput}
            onChange={(e) => setHealInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && applyHeal()}
          />
          <button className="btn-ghost btn-sm" style={{ flex: 1, color: '#5cb870' }} onClick={applyHeal}>
            Heal
          </button>
        </div>
      </div>

      {/* Set HP / Max HP */}
      <div style={styles.section}>
        <div style={styles.sectionLabel}>Set Values</div>
        <div style={styles.inputRow}>
          <input
            style={styles.numInput}
            type="number"
            placeholder="HP"
            value={hpInput}
            onChange={(e) => setHpInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && applySetHp()}
          />
          <button className="btn-ghost btn-sm" style={{ flex: 1 }} onClick={applySetHp}>Set HP</button>
        </div>
        <div style={styles.inputRow}>
          <input
            style={styles.numInput}
            type="number"
            placeholder="Max HP"
            value={maxHpInput}
            onChange={(e) => setMaxHpInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && applySetMaxHp()}
          />
          <button className="btn-ghost btn-sm" style={{ flex: 1 }} onClick={applySetMaxHp}>Set Max</button>
        </div>
      </div>

      {/* Temp HP */}
      <div style={styles.section}>
        <div style={styles.sectionLabel}>Temp HP <span style={styles.tempCurrent}>(current: {tempHp})</span></div>
        <div style={styles.inputRow}>
          <input
            style={styles.numInput}
            type="number"
            min="0"
            placeholder="0"
            value={tempInput}
            onChange={(e) => setTempInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && applyTempHp()}
          />
          <button className="btn-ghost btn-sm" style={{ flex: 1, color: '#7ab8e0' }} onClick={applyTempHp}>Add Temp</button>
          {tempHp > 0 && (
            <button className="btn-ghost btn-sm" onClick={() => updateToken(token.id, { tempHp: 0 })} title="Clear temp HP">✕</button>
          )}
        </div>
      </div>

      {/* ── Collapsible Token Settings ─────────────────────────────────── */}
      <button
        type="button"
        onClick={() => setSettingsOpen((v) => !v)}
        style={{
          width: '100%', background: '#1a2a48', border: '1px solid #4a3d28', borderRadius: 6,
          color: '#d4c9a8', fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
          letterSpacing: '0.06em', padding: '6px 10px', cursor: 'pointer',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}
      >
        <span>Token Settings</span>
        <span>{settingsOpen ? '▲' : '▼'}</span>
      </button>
      {settingsOpen && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '6px 0' }}>

          {/* Size + Colour */}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}
            onMouseDown={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <label style={{ color: '#9a8c73', fontSize: 10, fontWeight: 700 }}>Size</label>
            {[1, 2, 3, 4].map((s) => (
              <button
                key={s}
                type="button"
                onPointerDown={(e) => { e.stopPropagation(); updateToken(token.id, { size: s }); }}
                style={{
                  padding: '4px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                  border: '1px solid #4a3d28', borderRadius: 4, position: 'relative', zIndex: 1,
                  background: (token.size || 1) === s ? '#c084fc' : '#2a2a40',
                  color: (token.size || 1) === s ? '#1a1a2e' : '#9a8c73',
                }}
              >
                {s}×{s}
              </button>
            ))}
            <div style={{ flex: 1 }} />
            <input
              type="color"
              value={token.colour || '#c084fc'}
              onInput={(e) => updateToken(token.id, { colour: e.target.value })}
              onPointerDown={(e) => e.stopPropagation()}
              style={{ width: 28, height: 28, border: '1px solid #4a3d28', borderRadius: 4, background: 'none', cursor: 'pointer', padding: 0 }}
            />
          </div>

          {/* Initiative Bonus */}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}
            onMouseDown={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <label style={{ color: '#9a8c73', fontSize: 10, fontWeight: 700 }}>Initiative</label>
            <input
              type="number"
              value={token.initiativeBonus ?? 0}
              onChange={(e) => updateToken(token.id, { initiativeBonus: parseInt(e.target.value, 10) || 0 })}
              onPointerDown={(e) => e.stopPropagation()}
              style={{ width: 56, padding: '4px 6px', fontSize: 12, background: '#1a2a48', border: '1px solid #4a3d28', borderRadius: 4, color: '#d0d0e0', outline: 'none', textAlign: 'center' }}
            />
            <span style={{ color: '#6a5d45', fontSize: 10 }}>mod</span>
          </div>

          {/* D&D size category — drives pass-through rules (Tiny / 2+-size-diff) */}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}
            onMouseDown={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <label style={{ color: '#9a8c73', fontSize: 10, fontWeight: 700 }}>Category</label>
            <select
              value={token.sizeCategory || ''}
              onChange={(e) => updateToken(token.id, { sizeCategory: e.target.value || null })}
              onPointerDown={(e) => e.stopPropagation()}
              style={{ flex: 1, padding: '4px 6px', fontSize: 12, fontWeight: 600, background: '#2a2a40', color: '#d0d0e0', border: '1px solid #4a3d28', borderRadius: 4, cursor: 'pointer' }}
            >
              <option value="">Auto (from footprint)</option>
              <option value="tiny">Tiny</option>
              <option value="small">Small</option>
              <option value="medium">Medium</option>
              <option value="large">Large</option>
              <option value="huge">Huge</option>
              <option value="gargantuan">Gargantuan</option>
            </select>
          </div>

          {/* Friendly / Enemy toggle (NPCs and monsters) */}
          {(token.type === 'friendlyNPC' || token.type === 'monster') && (
            <div style={styles.section}>
              <div style={styles.sectionLabel}>Allegiance</div>
              <div style={styles.inputRow}>
                <button
                  className="btn-ghost btn-sm"
                  style={{ flex: 1, ...(token.type === 'friendlyNPC' ? { borderColor: '#5cb870', color: '#5cb870' } : {}) }}
                  onClick={() => updateToken(token.id, { type: 'friendlyNPC' })}
                >
                  Friendly
                </button>
                <button
                  className="btn-ghost btn-sm"
                  style={{ flex: 1, ...(token.type === 'monster' ? { borderColor: '#e07070', color: '#e07070' } : {}) }}
                  onClick={() => updateToken(token.id, { type: 'monster' })}
                >
                  Enemy
                </button>
              </div>
            </div>
          )}

          {/* Rotation (objects only) */}
          {token.type === 'object' && (
            <div style={styles.section}>
              <div style={styles.sectionLabel}>Rotation</div>
              <div style={styles.inputRow}>
                {[0, 90, 180, 270].map((deg) => (
                  <button
                    key={deg}
                    className="btn-ghost btn-sm"
                    style={{
                      flex: 1,
                      ...(( token.rotation || 0) === deg ? { borderColor: '#d4a847', color: '#d4a847' } : {}),
                    }}
                    onClick={() => updateToken(token.id, { rotation: deg })}
                  >
                    {deg}°
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Intersection toggle — objects only */}
          {token.type === 'object' && (
            <div style={styles.section}>
              <button
                className="btn-ghost btn-sm"
                style={{
                  width: '100%',
                  ...(token.onIntersection ? { borderColor: '#8bc34a', color: '#8bc34a' } : {}),
                }}
                onClick={() => updateToken(token.id, { onIntersection: !token.onIntersection })}
              >
                {token.onIntersection ? 'On Intersection' : 'On Cell'}
              </button>
            </div>
          )}

          {/* Sprite pack override — monsters & friendly NPCs only */}
          {(token.type === 'monster' || token.type === 'friendlyNPC') && (
            <div style={styles.section}>
              <div style={styles.sectionLabel}>
                Sprite Pack
                <span style={{ color: '#6a5d45', fontWeight: 400, textTransform: 'none', marginLeft: 6 }}>
                  {!token.packKey && monsterPackKey(token) ? `(auto: ${MONSTER_PACKS[monsterPackKey(token)]?.name})` : ''}
                </span>
              </div>
              <select
                style={{ ...styles.numInput, width: '100%', textAlign: 'left', fontSize: 12 }}
                value={token.packKey || ''}
                onChange={(e) => updateToken(token.id, { packKey: e.target.value || null })}
              >
                <option value="">— Auto / Procedural —</option>
                {Object.entries(THEMES).map(([theme, ids]) => (
                  <optgroup key={theme} label={theme}>
                    {ids.map((id) => (
                      <option key={id} value={id}>{MONSTER_PACKS[id]?.name || id}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
              {/* Sprite position & scale sliders */}
              <div style={{ marginTop: 8 }} onMouseDown={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                  <label style={{ color: '#9a8c73', fontSize: 10, width: 16 }}>X</label>
                  <input type="range" min={-50} max={50} value={Math.round((token.packOffsetX ?? 0) * 100)}
                    onChange={(e) => updateToken(token.id, { packOffsetX: +e.target.value / 100 })}
                    style={{ flex: 1, accentColor: '#c084fc' }} />
                  <span style={{ color: '#6a5d45', fontSize: 10, width: 32, textAlign: 'right' }}>{Math.round((token.packOffsetX ?? 0) * 100)}%</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                  <label style={{ color: '#9a8c73', fontSize: 10, width: 16 }}>Y</label>
                  <input type="range" min={-50} max={50} value={Math.round((token.packOffsetY ?? 0) * 100)}
                    onChange={(e) => updateToken(token.id, { packOffsetY: +e.target.value / 100 })}
                    style={{ flex: 1, accentColor: '#c084fc' }} />
                  <span style={{ color: '#6a5d45', fontSize: 10, width: 32, textAlign: 'right' }}>{Math.round((token.packOffsetY ?? 0) * 100)}%</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                  <label style={{ color: '#9a8c73', fontSize: 10, width: 16 }}>W</label>
                  <input type="range" min={50} max={250} value={Math.round((token.packWidthScale ?? 1) * 100)}
                    onChange={(e) => updateToken(token.id, { packWidthScale: +e.target.value / 100 })}
                    style={{ flex: 1, accentColor: '#c084fc' }} />
                  <span style={{ color: '#6a5d45', fontSize: 10, width: 32, textAlign: 'right' }}>{Math.round((token.packWidthScale ?? 1) * 100)}%</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <label style={{ color: '#9a8c73', fontSize: 10, width: 16 }}>H</label>
                  <input type="range" min={50} max={250} value={Math.round((token.packHeightScale ?? 1) * 100)}
                    onChange={(e) => updateToken(token.id, { packHeightScale: +e.target.value / 100 })}
                    style={{ flex: 1, accentColor: '#c084fc' }} />
                  <span style={{ color: '#6a5d45', fontSize: 10, width: 32, textAlign: 'right' }}>{Math.round((token.packHeightScale ?? 1) * 100)}%</span>
                </div>
              </div>
            </div>
          )}

          {/* Assign to player */}
          {Object.keys(players).length > 0 && (
            <div style={styles.section}>
              <div style={styles.sectionLabel}>Assign to Player</div>
              <div style={styles.inputRow}>
                <select
                  style={{ ...styles.numInput, width: '100%', textAlign: 'left', fontSize: 12 }}
                  value={token.ownerId || ''}
                  onChange={(e) => assignToken(token.id, e.target.value || null)}
                >
                  <option value="">— Unassigned —</option>
                  {Object.values(players).map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Buffs */}
      <div style={styles.section}>
        <div style={styles.sectionLabel}>Buffs & Effects</div>
        <BuffPanel token={token} />
      </div>

      {/* Shop binding — set this token as a merchant */}
      <div style={styles.section}>
        <div style={styles.sectionLabel}>Shop</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <select
            value={token.shopId || ''}
            onChange={(e) => updateToken(token.id, { shopId: e.target.value || null })}
            style={{
              flex: 1, padding: '6px 8px', background: '#0e1528',
              border: '1px solid #4a3d28', borderRadius: 4,
              color: '#f0e6d0', fontSize: 12, fontFamily: "'Press Start 2P', monospace",
            }}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <option value="">None</option>
            <option value="general">🏪 General Store</option>
            <option value="smithy">⚒ Smithy</option>
            <option value="alchemist">🧪 Alchemist</option>
          </select>
          <span style={{ fontSize: 10, color: '#9a8b6e', flex: 1, lineHeight: 1.3 }}>
            Players within 2 cells of a merchant see a shop button.
          </span>
        </div>
      </div>

      {/* Stealth DC — only meaningful when the 'stealthed' buff is on */}
      {buffs.some((b) => b.id === 'stealthed') && (
        <div style={styles.section}>
          <div style={styles.sectionLabel}>Stealth DC</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="number"
              min={1}
              max={40}
              value={token.stealthDC ?? ''}
              onChange={(e) => {
                const v = e.target.value === '' ? null : parseInt(e.target.value, 10);
                updateToken(token.id, { stealthDC: Number.isFinite(v) ? v : null });
              }}
              placeholder="—"
              style={{
                width: 70, padding: '6px 8px', background: '#0e1528',
                border: '1px solid #4a3d28', borderRadius: 4,
                color: '#c9b3e8', fontSize: 13, fontWeight: 700,
                fontFamily: "'Press Start 2P', monospace", textAlign: 'center',
              }}
              onPointerDown={(e) => e.stopPropagation()}
            />
            <span style={{ fontSize: 11, color: '#9a8b6e', flex: 1, lineHeight: 1.35 }}>
              Players whose perception roll ≥ this DC discover the token.
            </span>
          </div>
        </div>
      )}

      {/* Inventory — only for tokens linked to a player record (DM drops loot here) */}
      {token.ownerId && players?.[token.ownerId] && (
        <div style={styles.section}>
          <div style={styles.sectionLabel}>Inventory</div>
          <button
            className="btn-ghost btn-sm"
            style={{ width: '100%' }}
            onClick={() => setShowInventory(true)}
          >
            🎒 Open inventory & wallet
          </button>
        </div>
      )}
      {showInventory && token.ownerId && players?.[token.ownerId] && (
        <InventoryPanel
          inventory={players[token.ownerId].inventory || []}
          currency={players[token.ownerId].currency || {}}
          onInventoryChange={(next) => updatePlayer(token.ownerId, { inventory: next })}
          onCurrencyChange={(next)  => updatePlayer(token.ownerId, { currency: next })}
          editable
          title={`🎒 ${players[token.ownerId].charName || 'Player'} — Inventory`}
          onClose={() => setShowInventory(false)}
        />
      )}

      {/* Inspiration — RAW one-at-a-time toggle for player-linked tokens */}
      {token.ownerId && players?.[token.ownerId] && (() => {
        const hasInspiration = !!players[token.ownerId].inspiration;
        return (
          <div style={styles.section}>
            <button
              className="btn-ghost btn-sm"
              style={{
                width: '100%',
                ...(hasInspiration
                  ? { borderColor: '#fbbf24', color: '#fbbf24', background: 'rgba(251,191,36,0.10)' }
                  : {}),
              }}
              onClick={() => updatePlayer(token.ownerId, { inspiration: !hasInspiration })}
            >
              {hasInspiration ? '✨ Revoke Inspiration' : '✨ Grant Inspiration'}
            </button>
          </div>
        );
      })()}

      {/* Engaged toggle */}
      <div style={styles.section}>
        <button
          className="btn-ghost btn-sm"
          style={{ width: '100%', ...(token.engaged ? { borderColor: '#ef4444', color: '#ef4444', background: 'rgba(239,68,68,0.1)' } : {}) }}
          onClick={() => updateToken(token.id, { engaged: !token.engaged })}
        >
          {token.engaged ? '⚔ Engaged' : '⚔ Not Engaged'}
        </button>
      </div>

      {/* Conditions */}
      <div style={styles.section}>
        <div style={{ ...styles.sectionLabel, display: 'flex', justifyContent: 'space-between' }}>
          <span>Conditions</span>
          {conditions.length > 0 && (
            <button style={styles.clearBtn} onClick={clearConditions}>Clear all</button>
          )}
        </div>
        <div style={styles.condGrid}>
          {CONDITIONS.map((cond) => {
            const active = conditions.includes(cond.id);
            return (
              <Tooltip key={cond.id} text={cond.desc}>
                <button
                  style={{ ...styles.condBtn, ...(active ? styles.condActive : {}) }}
                  onClick={() => toggleCondition(cond.id)}
                >
                  <span style={styles.condIcon}>{cond.icon}</span>
                  <span style={styles.condLabel}>{cond.label}</span>
                </button>
              </Tooltip>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const styles = {
  inspector:    { padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 12, overflowY: 'auto', height: '100%' },
  empty:        { padding: 20, color: '#6a5d45', fontSize: 13, textAlign: 'center' },
  tokenHeader:  { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 },
  tokenDot:     { width: 12, height: 12, borderRadius: '50%', flexShrink: 0 },
  tokenName:    { fontWeight: 700, fontSize: 15, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  tokenNameInput: { fontWeight: 700, fontSize: 15, flex: 1, minWidth: 0, background: '#0e1528', border: '1px solid #4a3d28', borderRadius: 4, color: '#f0e6d0', padding: '3px 6px', outline: 'none', fontFamily: 'inherit', cursor: 'text' },
  tokenType:    { fontSize: 11, color: '#9a8b6e', textTransform: 'capitalize' },
  hpBarOuter:   { height: 8, background: '#1a2a48', borderRadius: 4, overflow: 'hidden', position: 'relative' },
  hpBarFill:    { height: '100%', borderRadius: 4, transition: 'width 0.3s, background 0.3s', position: 'absolute', top: 0, left: 0 },
  tempBar:      { height: '100%', background: '#7ab8e0', borderRadius: 4, position: 'absolute', top: 0, left: 0, opacity: 0.4 },
  hpNumbers:    { display: 'flex', alignItems: 'baseline', gap: 0 },
  hpSlash:      { color: '#9a8b6e', fontSize: 13 },
  tempDisplay:  { color: '#7ab8e0', fontSize: 12, marginLeft: 6 },
  section:      { display: 'flex', flexDirection: 'column', gap: 6 },
  sectionLabel: { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#9a8b6e' },
  inputRow:     { display: 'flex', gap: 6, alignItems: 'center' },
  numInput:     { width: 64, flexShrink: 0, background: '#1a2a48', border: '1px solid #4a3d28', borderRadius: 4, color: '#f0e6d0', padding: '6px 8px', fontSize: 14, outline: 'none', textAlign: 'center' },
  tempCurrent:  { color: '#7ab8e0', fontWeight: 400, textTransform: 'none', letterSpacing: 0 },
  clearBtn:     { background: 'transparent', border: 'none', color: '#9a8b6e', fontSize: 11, cursor: 'pointer', padding: 0, textDecoration: 'underline' },
  condGrid:     { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4 },
  condBtn:      { background: '#1a2a48', border: '1px solid #4a3d28', borderRadius: 6, padding: '5px 4px', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, transition: 'background 0.12s, border-color 0.12s' },
  condActive:   { background: '#3a1030', borderColor: '#c0392b', boxShadow: '0 0 6px rgba(233,69,96,0.3)' },
  condIcon:     { fontSize: 14 },
  condLabel:    { fontSize: 9, color: '#aaaacc', textAlign: 'center', lineHeight: 1.2 },
  concCheckPanel: { background: 'rgba(250,204,21,0.1)', border: '1px solid rgba(250,204,21,0.4)', borderRadius: 6, padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 6 },
  concCheckTitle: { fontSize: 12, fontWeight: 800, color: '#facc15', textTransform: 'uppercase', letterSpacing: '0.06em' },
  concCheckDesc:  { fontSize: 12, color: '#f0e6d0', lineHeight: 1.4 },
  concCheckBtns:  { display: 'flex', gap: 6 },
};
